import React from 'react';
import { BrandLogo, LOGO_URL } from '../components/BrandLogo';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Play } from 'lucide-react';

const BASE = 'https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts';

export default function FounderMessagePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </button>
          <BrandLogo className="h-12" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-5 md:px-8 pt-24 pb-16">
        {/* Page Title */}
        <div className="text-center mb-10">
          <p className="text-[#C41E3A] text-sm font-medium uppercase tracking-widest mb-3">Message from Founder</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="founder-page-title">
            A Message from the Founder
          </h1>
        </div>

        {/* ── The Letter — red bordered card ── */}
        <div className="border border-[#C41E3A]/30 rounded-2xl bg-zinc-900/20 p-6 md:p-10 mb-14" data-testid="founder-letter-card">

          {/* Portrait + Name */}
          <div className="flex flex-col items-center mb-10">
            <div className="w-40 h-52 md:w-48 md:h-64 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <img
                src={`${BASE}/038a1b964df1a01b_1000375010.webp`}
                alt="Srinagesh Mandala, Founder of BrandStage"
                className="w-full h-full object-cover object-top"
                data-testid="founder-portrait"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-white font-semibold text-lg">Srinagesh Mandala</p>
              <p className="text-zinc-400 text-sm">Founder, BrandStage &middot; Founder & Director, CONTINEX</p>
            </div>
          </div>

          {/* Letter body — refined typography for comfortable reading */}
          <div className="space-y-6 text-zinc-300 text-[15px] md:text-base leading-[1.85] tracking-[0.01em] text-justify" style={{ fontWeight: 200, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>

            <p>
              <span className="text-white/90 font-extralight italic">"Many businesses are better than the way they present themselves to the world."</span>
            </p>

            <p>
              This belief — shaped by years working with entrepreneurs, international organisations,
              and through founding CONTINEX in 2007 — is what inspired BrandStage.
            </p>

            <p>
              Digital marketing creates visibility, but lasting credibility requires something more:
              <span className="text-white"> consistent leadership presence, authentic stories and customer
              testimonials.</span> BrandStage brings these together in a <span className="text-white">living,
              video-first professional profile</span> that evolves with your business.
            </p>

            <p>
              I often think of BrandStage as a first-class lounge — a space where professionals
              discover one another, understand capabilities and begin meaningful conversations with
              confidence. Where credibility precedes the introduction. And just as with a first-class
              lounge, <span className="text-white">your profile is private — visible only to those you choose to share it with.</span>
            </p>

            {/* Mission highlight — same alignment, subtle accent */}
            <div className="border-l-2 border-[#C41E3A]/40 pl-6 py-2">
              <p className="text-white/90 font-extralight text-lg md:text-xl leading-relaxed tracking-[0.01em]">
                Our ambition is to make this experience accessible to businesses and professionals
                everywhere, regardless of size or location.
              </p>
            </div>

            {/* Closing quote */}
            <p className="text-white/80 font-extralight italic text-lg md:text-xl leading-relaxed tracking-[0.01em]">
              "The future will belong not only to businesses that create value, but to those
              that know how to make that value visible."
            </p>

            <p>
              Thank you for believing in our efforts. We hope to earn your continued trust as we
              work to make BrandStage a meaningful platform for businesses worldwide.
            </p>
          </div>

          {/* Signature — inside the card */}
          <div className="mt-10 pt-8 border-t border-zinc-800 flex flex-col items-center gap-3" data-testid="founder-signature-block">
            <img
              src={LOGO_URL}
              alt="BrandStage"
              className="h-9 opacity-50"
              data-testid="founder-signature-logo"
            />
            <div className="text-center">
              <p className="text-white font-semibold text-lg">Srinagesh Mandala</p>
              <p className="text-zinc-400 text-sm">Founder, BrandStage &middot; Founder & Director, CONTINEX</p>
            </div>
          </div>
        </div>
        {/* ── End of Letter Card ── */}

        {/* Experience BrandStage CTA */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center mb-14" data-testid="founder-cta-section">
          <h3 className="text-xl font-bold text-white mb-3">Experience BrandStage</h3>
          <p className="text-zinc-400 font-normal mb-6">
            See what a complete BrandStage profile looks like
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/p/cinimage')}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2"
              data-testid="view-sample-profile-btn"
            >
              View Sample Profile
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/register')}
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-xl font-medium transition-colors border border-zinc-700"
              data-testid="create-profile-btn"
            >
              Create Your Profile
            </button>
          </div>
        </div>

        {/* ShiftHappens podcast */}
        <section className="border-t border-zinc-800 pt-12 mb-14" data-testid="founder-podcast-section">
          <p className="text-[#C41E3A] text-xs tracking-[0.2em] uppercase mb-3 flex items-center gap-2">
            <Play className="w-3.5 h-3.5" /> Beyond the message
          </p>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div className="max-w-[56ch]">
              <h2 className="text-base md:text-lg text-white font-semibold mb-1">ShiftHappens: the podcast</h2>
              <p className="text-zinc-400 text-sm font-normal leading-relaxed">
                Many of the conversations that shaped this thinking continue on ShiftHappens — the podcast I host
                with business leaders on the shifts redefining global trade.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/podcast')}
              className="inline-flex items-center gap-2 text-sm text-white hover:text-[#C41E3A] font-medium transition-colors"
              data-testid="founder-podcast-link"
            >
              More episodes <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="w-full md:w-[60%] mx-auto aspect-video rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900" data-testid="founder-podcast-video">
            <iframe
              src="https://www.youtube-nocookie.com/embed/Vwe_-Y3Kz6o?rel=0&modestbranding=1"
              title="ShiftHappens podcast"
              className="w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">
            &copy; {new Date().getFullYear()} BrandStage. Powered by CONTINEX.
          </p>
        </div>
      </footer>
    </div>
  );
}
